import { useMutation } from '@apollo/client'
import { Container } from '@mui/material'
import { EmploymentAnswer } from 'candidate/components/EmploymentQuestion'
import Question from 'candidate/components/Question'
import { SubmitIntake } from 'candidate/graphql/candidateIntakes.gql'
import { CandidateIntake, ChoiceBucket } from 'candidate/models/intake'
import { ProgressBar } from 'candidate/pages/Intakes/ProgressBar'
import { ProgressStepper } from 'candidate/pages/Intakes/ProgressStepper'
import { candidateRouteConfig } from 'candidate/routes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import { getPxAsRem } from 'shared/utils'

import type { FC } from 'react'

function shouldSkip(skipMap: Record<string, string>, response: any): boolean {
    let result = false

    if (Array.isArray(response)) {
        result = response.every((x: string | ChoiceBucket) => {
            const choiceBucketResponse = x as ChoiceBucket
            if (choiceBucketResponse) {
                return !!skipMap[choiceBucketResponse.choice]
            } else {
                x = x as string
                return !!skipMap[x]
            }
        })
    } else {
        const choiceBucketResponse = response as ChoiceBucket
        if (choiceBucketResponse) {
            result = !!skipMap[choiceBucketResponse.choice]
        } else {
            result = !!skipMap[response]
        }
    }

    return result
}

function getSkipTo(skipMap: Record<string, string>, response: any): string {
    let result = ''

    if (Array.isArray(response)) {
        // NOTE - We are assuming each response maps to the same skip. We don't
        // support multiple_response with different skips.

        const choiceBucketResponse = response[0] as ChoiceBucket
        if (choiceBucketResponse) {
            result = skipMap[choiceBucketResponse.choice]
        } else {
            result = skipMap[response[0]]
        }
    } else {
        const choiceBucketResponse = response as ChoiceBucket
        if (choiceBucketResponse) {
            result = skipMap[choiceBucketResponse.choice]
        } else {
            result = skipMap[response]
        }
    }

    return result
}

interface Props {
    candidateIntake: Partial<CandidateIntake>
    existingEmploymentAnswer?: EmploymentAnswer
    existingWorkExperienceId?: string
    lastIntakeInPart?: boolean
}

const ActiveQuestion: FC<Props> = ({
    candidateIntake,
    existingEmploymentAnswer,
    existingWorkExperienceId,
}: Props) => {
    const isMobile = useIsMobile()
    const navigate = useNavigate()
    const toast = useToast()

    const questions = useMemo(
        () => candidateIntake.intake?.questions ?? [],
        [candidateIntake]
    )
    const [currentStep, setCurrentStep] = useState(0)
    const intakeName = candidateIntake?.intake?.displayName ?? ''
    const [activeQuestionIndex, setActiveQuestion] = useState(0)
    const [skipIndex, setSkipIndex] = useState(-1)
    const [answers, setAnswers] = useState<Record<any, any>[]>([])
    const [questionIndexAnswered, setQuestionIndexAnswered] = useState<
        number[]
    >([-1])
    const [intakeComplete, setIntakeComplete] = useState(false)
    const [skipTo, setSkipTo] = useState<string>('')

    const [submitIntake] = useMutation(SubmitIntake, {
        onCompleted(data) {
            setIntakeComplete(true)
        },
        onError(error) {
            console.error('intake submission error: ', error)
            // TODO: more detailed error message?
            toast({
                title: 'Something went wrong',
                message: 'Please try again',
            })
        },
    })

    const handleIndexChange = (index: number) => {
        if (questionIndexAnswered.includes(index)) {
            setCurrentStep(index)
            setActiveQuestion(index)
        }
    }

    const updateAnswers = useCallback(
        (answer: Record<any, any>): void => {
            setCurrentStep(activeQuestionIndex + 1)
            answers[activeQuestionIndex] = {
                questionId: questions[activeQuestionIndex].id,
                questionInternalName:
                    questions[activeQuestionIndex].internalName,
                answerData: answer,
            }
            setAnswers(answers)
            if (activeQuestionIndex === questions.length - 1) {
                const input = {
                    internalIntakeName:
                        candidateIntake.intake?.intakePart.internalName,
                    candidateIntakeId: candidateIntake.id,
                    answers,
                }
                submitIntake({
                    variables: { input },
                })
                return
            }
            setActiveQuestion(activeQuestionIndex + 1)

            // NOTE - Handle skipping based on response.
            const { questionData } = questions[activeQuestionIndex]

            const response = answer['response']
            setQuestionIndexAnswered((prev) => [...prev, activeQuestionIndex])
            if (
                questionData.skip?.byAnswer &&
                shouldSkip(questionData.skip.byAnswer, response)
            ) {
                setSkipTo(getSkipTo(questionData.skip.byAnswer, response))
                setSkipIndex(activeQuestionIndex + 1)
            }
        },
        [activeQuestionIndex, answers, candidateIntake, questions, submitIntake]
    )

    useEffect(() => {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = 'Changes will be lost!'
        }
    }, [])

    useEffect(() => {
        return () => {
            window.onbeforeunload = null
        }
    }, [])

    useEffect(() => {
        if (
            questions[activeQuestionIndex] &&
            questions[activeQuestionIndex].internalName === skipTo
        ) {
            setSkipTo('')
            return
        }
        if (skipTo) {
            updateAnswers({ response: { choice: 'N/A' } })
        }
    }, [activeQuestionIndex, questions, skipTo, updateAnswers])

    if (intakeComplete) {
        navigate(candidateRouteConfig.intakes.complete.path, { replace: true })
        return null
    }

    if (skipTo && questions[activeQuestionIndex]?.internalName !== skipTo) {
        return null
    }

    return (
        <>
            {/* KV TODO: Add Prompt message if user tries to quit the intake */}
            <Container maxWidth="md" sx={{ flex: 1, paddingY: getPxAsRem(36) }}>
                {!isMobile && (
                    <ProgressBar
                        steps={questions.length}
                        currentStep={currentStep}
                        handleStep={handleIndexChange}
                        intakeName={intakeName}
                        skipIndex={skipIndex}
                    />
                )}

                {questions[activeQuestionIndex] && (
                    <Question
                        activeQuestionIndex={activeQuestionIndex}
                        intakeName={intakeName}
                        answers={answers}
                        question={questions[activeQuestionIndex]}
                        updateAnswers={updateAnswers}
                        existingEmploymentAnswer={existingEmploymentAnswer}
                        existingWorkExperienceId={existingWorkExperienceId}
                        skipCallback={
                            !!questions[activeQuestionIndex]?.questionData?.skip
                                ?.asNotApplicable
                                ? () =>
                                      setSkipTo(
                                          questions[activeQuestionIndex]
                                              .questionData.skip.asNotApplicable
                                      )
                                : undefined
                        }
                        skipText={
                            questions[activeQuestionIndex]?.questionData?.skip
                                ?.skipText
                        }
                    />
                )}
            </Container>

            {isMobile && (
                <ProgressStepper
                    steps={questions.length}
                    currentStep={currentStep}
                    handleStep={handleIndexChange}
                    answers={answers}
                />
            )}
        </>
    )
}

export default ActiveQuestion
